import { EmptyState, FlexColumn, Loading } from '@gorila-shared-ui/components';
import { Accordion, Panel } from 'baseui/accordion';
import { useEffect } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import { getHistoricState } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import {
  activeReportRouteState,
  loadingReportRouteState,
  reportEndDateState,
  reportHistoricStates,
  reportSelectedAssetIdsState,
  reportStartDateState,
} from '../../../storage/reports';
import { HistoricState } from '../../../types/currentState';
import { RouteReportInfo } from './RouteReportInfo';
import { RouteReportTableFromAsset } from './RouteReportTableFromAsset';
import { RouteReportTitle } from './RouteReportTitle';

export function RouteReports() {
  const { css } = useStyles();
  const selectedAssetIds = useRecoilValue(reportSelectedAssetIdsState);
  const endDate = useRecoilValue(reportEndDateState);
  const startDate = useRecoilValue(reportStartDateState);
  const apiParams = useRecoilValue(apiParamsState);
  const setActiveRoute = useSetRecoilState(activeReportRouteState);
  const resetActiveRoute = useResetRecoilState(activeReportRouteState);
  const [loadingReportRoute, setLoadingReportRoute] = useRecoilState(loadingReportRouteState);
  const setReportHistorics = useSetRecoilState(reportHistoricStates);
  const resetReportHistorics = useResetRecoilState(reportHistoricStates);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);

  useEffect(() => {
    return () => {
      resetReportHistorics();
    };
  }, []);

  useEffect(() => {
    if (!selectedAssetIds?.length || !startDate || !endDate || !apiParams) {
      resetActiveRoute();
      return;
    }

    const promises = selectedAssetIds.map((assetId) =>
      getHistoricState(
        apiParams.clientId,
        assetId,
        startDate,
        endDate,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
      ),
    );

    const loadHistoricStates = async () => {
      setLoadingReportRoute(true);
      const responses = await Promise.allSettled(promises);

      const newHistorics: { [key: string]: { states: HistoricState[]; onlyEvents: boolean } } = {};
      responses.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const {
            value: { historicStates, error },
          } = result;
          if (!error) {
            newHistorics[selectedAssetIds[index]] = { states: historicStates ?? [], onlyEvents: false };
          }
        }
      });
      setReportHistorics(newHistorics);

      if (newHistorics[selectedAssetIds[0]]) {
        setActiveRoute(selectedAssetIds[0]);
      }
      setLoadingReportRoute(false);
    };

    loadHistoricStates();
  }, [selectedAssetIds, startDate, endDate]);

  if (loadingReportRoute) {
    return <Loading />;
  }

  if (!selectedAssetIds?.length) {
    return <EmptyState title="Selecciona un activo para visualizar el reporte." />;
  }

  return (
    <FlexColumn
      gap={0}
      classNames={css({ flex: 1, overflow: 'hidden' })}
    >
      <AutoSizer>
        {({ width, height }) => (
          <div style={{ width, height, overflow: 'auto' }}>
            <Accordion
              initialState={{ expanded: [selectedAssetIds[0]] }}
              onChange={({ expanded }) => setActiveRoute(expanded.length ? (expanded[0] as string) : undefined)}
            >
              {selectedAssetIds.map((assetId) => (
                <Panel
                  key={assetId}
                  title={<RouteReportTitle assetId={assetId} />}
                  overrides={{
                    Content: {
                      style: {
                        paddingTop: 0,
                      },
                    },
                  }}
                >
                  <FlexColumn>
                    <RouteReportInfo assetId={assetId} />
                    <RouteReportTableFromAsset assetId={assetId} />
                  </FlexColumn>
                </Panel>
              ))}
            </Accordion>
          </div>
        )}
      </AutoSizer>
    </FlexColumn>
  );
}
